import { useCallback, useEffect, useRef, useState } from 'react'
import { getOutlookWorkspaceOAuthUrl, disconnectOutlookWorkspace } from '../services/outlookWorkspaceApi.js'
import { connectErrorCopy } from '../lib/connectErrorCopy'
import { markConnectPending, clearConnectPending, takeAbandonedConnect } from '../lib/pendingConnect'
import { toast } from '@/components/ui/use-toast'

export type UseOutlookWorkspaceIntegrationReturn = {
  connecting: boolean
  disconnecting: boolean
  showExplainer: boolean
  openConnectModal: () => void
  closeConnectModal: () => void
  confirmConnect: () => void
  disconnect: () => Promise<void>
}

// Mirrors google-workspace/hooks/useGoogleWorkspaceIntegration.ts. Outcomes
// surface as toasts rather than the inline FlashBanner the other provider
// cards use, so a connect result is visible wherever the rep is looking.
export function useOutlookWorkspaceIntegration(workspaceId: string, onRefresh?: () => void): UseOutlookWorkspaceIntegrationReturn {
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [showExplainer, setShowExplainer] = useState(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  const reportAbandonedConnect = useCallback(() => {
    if (!takeAbandonedConnect()) return
    setConnecting(false)
    setShowExplainer(false)
    toast({
      variant: 'warning',
      title: 'Outlook Workspace not connected',
      description: 'You came back before finishing. You can connect whenever you\'re ready.',
    })
  }, [])

  // Read ?outlookWorkspace= on return from OAuth, then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const outcome = params.get('outlookWorkspace')

    if (outcome !== 'connected' && outcome !== 'error') {
      reportAbandonedConnect()
      return
    }

    clearConnectPending()

    if (outcome === 'connected') {
      toast({ variant: 'success', title: 'Outlook Workspace connected', description: 'Zeph can now use your Outlook mail and calendar.' })
      onRefreshRef.current?.()
    } else {
      const reason = params.get('reason')
      console.error('[outlook-workspace] connect failed', { reason })
      toast({
        variant: 'destructive',
        title: "Couldn't connect Outlook Workspace",
        description: connectErrorCopy(reason),
      })
    }

    params.delete('outlookWorkspace')
    params.delete('reason')
    const newSearch = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`)
  }, [reportAbandonedConnect])

  useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) reportAbandonedConnect()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [reportAbandonedConnect])

  const openConnectModal = useCallback(() => setShowExplainer(true), [])
  const closeConnectModal = useCallback(() => setShowExplainer(false), [])

  const confirmConnect = useCallback(async () => {
    setConnecting(true)
    try {
      const url = await getOutlookWorkspaceOAuthUrl(workspaceId)
      markConnectPending()
      window.location.href = url
    } catch (err) {
      setConnecting(false)
      setShowExplainer(false)
      clearConnectPending()
      console.error('[outlook-workspace] failed to start OAuth', err)
      toast({
        variant: 'destructive',
        title: "Couldn't connect Outlook Workspace",
        description: 'Zeph couldn\'t reach Microsoft just now. Please try again.',
      })
    }
  }, [workspaceId])

  const disconnect = useCallback(async () => {
    setDisconnecting(true)
    try {
      await disconnectOutlookWorkspace(workspaceId)
      toast({ variant: 'success', title: 'Outlook Workspace disconnected', description: 'Zeph no longer has access to your Outlook mail or calendar.' })
    } catch (err) {
      console.error('[outlook-workspace] disconnect failed', err)
      toast({
        variant: 'destructive',
        title: "Couldn't disconnect Outlook Workspace",
        description: 'Your account is still connected. Please try again.',
      })
    } finally {
      setDisconnecting(false)
    }
  }, [workspaceId])

  return {
    connecting,
    disconnecting,
    showExplainer,
    openConnectModal,
    closeConnectModal,
    confirmConnect,
    disconnect,
  }
}
